import {
  type ApolloError,
  type DocumentNode,
  type FetchResult,
  gql,
  useMutation,
} from "@apollo/client";
import { noop } from "lodash";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type RefetchEntry =
  | DocumentNode
  | { query: DocumentNode; variables?: Record<string, any> };

export type UserCompanyAssignmentInput = {
  userId: string;
  companyId: string;
  role?: string;
};

export const useUpdateCompanyAccessUsers = (
  refetchQueries: RefetchEntry[] = [],
): [
  (
    companyAssignments: UserCompanyAssignmentInput[],
    assignmentsToRemove: UserCompanyAssignmentInput[],
    onSuccess?: () => void,
    onError?: () => void,
  ) => Promise<FetchResult | undefined>,
  {
    data: UpdateCompanyAccessUsersResponse;
    loading: boolean;
    error?: ApolloError;
  },
] => {
  const { t } = useTranslation();
  const [updateCompanyAccessUsers, { data, loading, error }] = useMutation(
    UPDATE_COMPANY_ACCESS_USERS_MUTATION,
    {
      refetchQueries: refetchQueries.map((entry) =>
        "query" in (entry as any)
          ? (entry as any)
          : { query: entry as DocumentNode },
      ),
    },
  );

  const handleUpdateCompanyAccessUsers = async (
    companyAssignments: UserCompanyAssignmentInput[],
    assignmentsToRemove: UserCompanyAssignmentInput[] = [],
    onSuccess: () => void = noop,
    onError: () => void = noop,
  ) => {
    const toastId = toast.loading(t("generic_loading"));

    try {
      const result = await updateCompanyAccessUsers({
        variables: { companyAssignments, assignmentsToRemove },
      });

      toast.success(t("generic_success"), { id: toastId });
      onSuccess();

      return result;
    } catch (error) {
      toast.error(t("generic_error"), { id: toastId, richColors: true });
      onError();
    }
  };

  return [handleUpdateCompanyAccessUsers, { data, loading, error }];
};

export const UPDATE_COMPANY_ACCESS_USERS_MUTATION = gql`
  mutation UpdateCompanyAccessUsers(
    $companyAssignments: [UserCompanyAssignmentInput!]!
    $assignmentsToRemove: [UserCompanyAssignmentInput!]!
  ) {
    updateCompanyAccessUsers(
      companyAssignments: $companyAssignments
      assignmentsToRemove: $assignmentsToRemove
    ) {
      addedAssignments
      removedAssignments
    }
  }
`;

export type UpdateCompanyAccessUsersResponse = {
  addedAssignments: unknown[];
  removedAssignments: unknown[];
};
